// src/pages/admin/AdminDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader      from '../../components/dashboard/DashboardHeader';
import DashboardMetrics     from '../../components/dashboard/DashboardMetrics';
import SignedContractsTable from '../../components/dashboard/SignedContractsTable';
import NewAnalysisModal     from '../../components/dashboard/NewAnalysisModal';
import FinancialDetailsTable from '../../components/dashboard/FinancialDetailsTable';
import OptimizablePDLTable from '../../components/dashboard/OptimizablePDLTable';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const MOIS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
                 'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

function buildPeriodParams(period) {
  if (!period) return '';
  if (period.type === 'month' && period.year && period.month)
    return `year=${period.year}&month=${period.month}`;
  if (period.type === 'year' && period.year)
    return `year=${period.year}`;
  if (period.type === 'custom' && period.startDate && period.endDate)
    return `startDate=${period.startDate}&endDate=${period.endDate}`;
  return '';
}

const DEFAULT_METRICS = {
  courtage:     { previsionnel: 0, encaisse: 0 },
  acheminement: { previsionnel: 0, encaisse: 0 },
  cspe:         { previsionnel: 0, encaisse: 0 },
  ticgn:        { previsionnel: 0, encaisse: 0 },
  global:       { previsionnel: 0, encaisse: 0 },
};

// ✅ On force un YEAR pour les endpoints financiers CSPE
function getYearFromPeriod(period) {
  if (!period) return new Date().getFullYear();
  if (period.type === 'year' && period.year) return period.year;
  if (period.type === 'month' && period.year) return period.year;
  // custom : on prend l’année du startDate si dispo, sinon année courante
  if (period.type === 'custom' && period.startDate) {
    const d = new Date(period.startDate);
    if (!isNaN(d.getTime())) return d.getFullYear();
  }
  return new Date().getFullYear();
}

// ─── Composant principal ──────────────────────────────────────────────────────
const AdminDashboard = ({ session }) => {
  const navigate = useNavigate();
  const now = new Date();

  // ── État ──
  const [selectedPeriod, setSelectedPeriod] = useState({
    type:  'month',
    year:  now.getFullYear(),
    month: now.getMonth() + 1,
  });

  const [signedContracts, setSignedContracts] = useState([]);
  const [existingOppIds,  setExistingOppIds]  = useState({});
  const [metrics,         setMetrics]         = useState(DEFAULT_METRICS);
  const [isModalOpen,     setIsModalOpen]     = useState(false);
  const [loading,         setLoading]         = useState(true);

  // ✅ Nouvel onglet
  const [financeTab, setFinanceTab] = useState('overview'); // overview | details
  const [financialDetails, setFinancialDetails] = useState({
    year: null,
    total_previsionnel: 0,
    rows: [],
  });

  // ────────────────────────────────────────────────────────────────────────────
  // ✅ Préchargement opportunités — jamais bloquant
  // ────────────────────────────────────────────────────────────────────────────
  const preloadOpportunityIds = useCallback(async (contracts) => {
    if (!contracts || contracts.length === 0) return;

    const accountIds = [...new Set(
      contracts.map(c => c.account_id).filter(Boolean)
    )];
    if (accountIds.length === 0) return;

    try {
      const results = await Promise.allSettled(
        accountIds.map(accountId =>
          fetch(`/api/opportunities?account_id=${accountId}`, {
            headers: { Authorization: `Bearer ${session?.access_token}` },
          }).then(r => r.ok ? r.json() : null)
        )
      );

      const accountToOppId = {};
      results.forEach((result, idx) => {
        if (result.status === 'fulfilled' && result.value?.data?.length > 0) {
          accountToOppId[accountIds[idx]] = result.value.data[0].id;
        }
      });

      const oppMap = {};
      contracts.forEach(contract => {
        if (contract.account_id && accountToOppId[contract.account_id]) {
          oppMap[contract.id] = accountToOppId[contract.account_id];
        }
      });

      setExistingOppIds(oppMap);
    } catch (err) {
      console.warn('[AdminDashboard] Préchargement opportunités ignoré :', err.message);
    }
  }, [session]);

  // ────────────────────────────────────────────────────────────────────────────
  // Fetch principal — contrats + métriques + détails financiers
  // ────────────────────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${session?.access_token}` };

      // ── 1. Contrats signés (garde ton period params) ───────────────────────
      const p = buildPeriodParams(selectedPeriod);
      const contractsRes  = await fetch(
        `/api/documents?type=tax_contract&status=signed${p ? `&${p}` : ''}`,
        { headers }
      );
      const contractsJson = await contractsRes.json();
      const contracts     = contractsJson.data || [];
      setSignedContracts(contracts);

      // ── 2. Métriques globales (⚠️ endpoint attend year uniquement) ─────────
      try {
        const year = getYearFromPeriod(selectedPeriod);

        const metricsRes  = await fetch(
          `/api/opportunities/global-metrics?year=${year}`,
          { headers }
        );
        const metricsJson = await metricsRes.json();

        if (metricsJson.success && metricsJson.data) {
          const raw = metricsJson.data;
          setMetrics({
            courtage:     { ...DEFAULT_METRICS.courtage,     ...(raw.courtage     || {}) },
            acheminement: { ...DEFAULT_METRICS.acheminement, ...(raw.acheminement || {}) },
            cspe:         { ...DEFAULT_METRICS.cspe,         ...(raw.cspe   || raw.taxes || {}) },
            ticgn:        { ...DEFAULT_METRICS.ticgn,        ...(raw.ticgn        || {}) },
            global:       { ...DEFAULT_METRICS.global,       ...(raw.global       || {}) },
          });
        } else {
          // garde les defaults
          setMetrics(DEFAULT_METRICS);
        }
      } catch (metricsErr) {
        console.warn('[AdminDashboard] Métriques indisponibles :', metricsErr.message);
        setMetrics(DEFAULT_METRICS);
      }

      // ── 3. Détails financiers (nouvel endpoint) ────────────────────────────
      try {
        const year = getYearFromPeriod(selectedPeriod);
        const detailsRes = await fetch(
          `/api/opportunities/financial-details?year=${year}`,
          { headers }
        );
        const detailsJson = await detailsRes.json();
        if (detailsJson.success && detailsJson.data) {
          setFinancialDetails(detailsJson.data);
        } else {
          setFinancialDetails({ year, total_previsionnel: 0, rows: [] });
        }
      } catch (detailsErr) {
        console.warn('[AdminDashboard] Détails financiers indisponibles :', detailsErr.message);
        const year = getYearFromPeriod(selectedPeriod);
        setFinancialDetails({ year, total_previsionnel: 0, rows: [] });
      }

      // ── 4. Pré-charger opportunités (non-bloquant) ──────────────────────────
      preloadOpportunityIds(contracts);

    } catch (err) {
      console.error('[AdminDashboard] Erreur fetch principal :', err);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, session, preloadOpportunityIds]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Rendu ──────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      {/* Header compact */}
      <DashboardHeader onNewAnalysis={() => setIsModalOpen(true)} />

      {/* Métriques */}
      <div className="flex-shrink-0 px-3 pt-2">
        <DashboardMetrics
          selectedPeriod={selectedPeriod}
          onPeriodChange={setSelectedPeriod}
          metrics={metrics}
        />
      </div>

      {/* Corps scrollable */}
      <div
        className="flex-1 overflow-y-auto px-3 pb-3 space-y-2"
        style={{ maxHeight: 'calc(100vh - 42px)' }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-600" />
            <span className="ml-2 text-xs text-gray-500">Chargement…</span>
          </div>
        ) : (
          <>
            {/* Onglets : Aperçu / Détails financiers */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 flex items-center justify-between">
              <div className="text-[11px] font-semibold text-gray-700">
                Finance
              </div>

              <div className="inline-flex rounded-md border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setFinanceTab('overview')}
                  className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    financeTab === 'overview'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Aperçu
                </button>
                <button
                  type="button"
                  onClick={() => setFinanceTab('details')}
                  className={`px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                    financeTab === 'details'
                      ? 'bg-teal-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Détails financiers
                </button>
              </div>
            </div>

            {/* Contenu selon onglet */}
            {financeTab === 'overview' ? (
              <SignedContractsTable
                contracts={signedContracts}
                selectedPeriod={selectedPeriod}
                session={session}
                existingOppIds={existingOppIds}
              />
            ) : (
              <FinancialDetailsTable
                year={financialDetails?.year || getYearFromPeriod(selectedPeriod)}
                totalPrevisionnel={financialDetails?.total_previsionnel || 0}
                rows={financialDetails?.rows || []}
              />
            )}

            <div className="mt-6">
              <OptimizablePDLTable />
            </div>
          </>
        )}
      </div>

      {/* Modal nouvelle analyse */}
      <NewAnalysisModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        session={session}
      />
    </div>
  );
};

export default AdminDashboard;
